import { type Database } from "@fitness-app/supabase";

import { type Ingredient, type IngredientWithPortion } from "./Ingredient";
import { type DishInMeal } from "./MealsPlan";

export type DishEntity = Database["public"]["Tables"]["dish"]["Row"];
export type DishEmbeddingEntity = Database["public"]["Tables"]["dish_embedding"]["Row"];
export type LatestSelectedProductsEntity = Database["public"]["Tables"]["latest_selected_products"]["Row"];
export type MealTypeEnum = Database["public"]["Enums"]["MealType"];
export type DishTypeEnum = Database["public"]["Enums"]["DishType"];
export type KitchenTypeEnum = Database["public"]["Enums"]["KitchenType"];
export type SensitiveIngredientEnum = Database["public"]["Enums"]["SensitiveIngredient"];

export const MealType: Record<Database["public"]["Enums"]["MealType"], Database["public"]["Enums"]["MealType"]> = {
  BREAKFAST: "BREAKFAST",
  SECOND_BREAKFAST: "SECOND_BREAKFAST",
  LUNCH: "LUNCH",
  DINNER: "DINNER",
  SNACK: "SNACK",
  SUPPER: "SUPPER",
} as const;

export const SensitiveIngredient: Record<
  Database["public"]["Enums"]["SensitiveIngredient"],
  Database["public"]["Enums"]["SensitiveIngredient"]
> = {
  MEAT: "MEAT",
  FISH: "FISH",
  SOY: "SOY",
  TREE_NUTS: "TREE_NUTS",
  SHELLFISH: "SHELLFISH",
  EGGS: "EGGS",
  DAIRY: "DAIRY",
  GLUTEN: "GLUTEN",
  PEANUTS: "PEANUTS",
};

export const DishType: Record<Database["public"]["Enums"]["DishType"], Database["public"]["Enums"]["DishType"]> = {
  KETO: "KETO",
  VEGETARIAN: "VEGETARIAN",
  SEMI_VEGETARIAN: "SEMI_VEGETARIAN",
  VEGAN: "VEGAN",
  GLUTEN_FREE: "GLUTEN_FREE",
  DIABETIC: "DIABETIC",
  DAIRY_FREE: "DAIRY_FREE",
  HIGH_PROTEIN: "HIGH_PROTEIN",
  HIGH_FIBER: "HIGH_FIBER",
  LOW_CARB: "LOW_CARB",
  LOW_FAT: "LOW_FAT",
  LACTOSE_FREE: "LACTOSE_FREE",
  LOW_CHOLESTEROL: "LOW_CHOLESTEROL",
  PALEO: "PALEO",
  LOW_FOODMAP: "LOW_FOODMAP",
};

export const KitchenType: Record<
  Database["public"]["Enums"]["KitchenType"],
  Database["public"]["Enums"]["KitchenType"]
> = {
  POLISH: "POLISH",
  ITALIAN: "ITALIAN",
  SPANISH: "SPANISH",
  FRENCH: "FRENCH",
  MEXICAN: "MEXICAN",
  ASIAN: "ASIAN",
  AMERICAN: "AMERICAN",
  GREEK: "GREEK",
  GERMAN: "GERMAN",
  THAI: "THAI",
  BRITISH: "BRITISH",
};
/**
 * Dish tags organized by category:
 * A. Eliminacja składników (Ingredient elimination)
 * B. Style żywienia (Eating styles)
 * C. Cele odżywcze / wartości energetyczne (Nutritional goals / energy values)
 * D. Cechy przygotowania / praktyczne (Preparation features / practical)
 * E. Jednostki chorobowe / stany zdrowia (Health conditions)
 * F. Model posiłku (Meal type)
 * G. Sezonowość (Seasonality)
 * + Review tags (AI uncertainty flags)
 */
export const dishTags = [
  // A. Eliminacja składników
  "bezglutenowy", // no gluten (incl. hidden in sauces, breading)
  "bezlaktozowy", // no lactose (lactose-free products allowed)
  "beznabialu", // no dairy at all (even lactose-free)
  "bezjajek", // no eggs or egg products
  "bezorzechow", // no nuts or nut products
  "bezryb", // no fish or seafood
  "bezsoi", // no soy or soy products
  "beznasion", // no seeds

  // B. Style żywienia
  "weganski", // no animal products (incl. honey, gelatin, cochineal)
  "wegetarianski", // no meat/fish, dairy/eggs allowed

  // C. Cele odżywcze / wartości energetyczne
  "lowcarb", // <15% energy from carbs
  "highcarb", // >55% energy from carbs
  "niskokaloryczny", // ≤250 kcal per serving
  "sredniokaloryczny", // 251-599 kcal per serving
  "wysokokaloryczny", // ≥600 kcal per serving
  "wysokoobjetosciowy", // ≥350g total weight
  "niskoobjetosciowy", // ≤200g total weight

  // D. Cechy przygotowania / praktyczne
  "bezgotowania", // no thermal processing (salads, skyr with fruits)
  "szybki", // prep time ≤10 minutes
  "fermentowany", // contains fermented ingredients (pickles, yogurt)

  // E. Jednostki chorobowe / stany zdrowia
  "karmieniepiersia", // safe for breastfeeding (no colic-risk, low allergens)
  "sibo", // lowFODMAP compatible
  "histamina", // no high-histamine foods
  "cukrzyca", // low glycemic load, balanced macros
  "ibs", // lowFODMAP compatible
  "hashimoto", // no soy, limited goitrogens
  "ciaza", // pregnancy-safe (no raw meat, liver, unpasteurized cheese)

  // F. Model posiłku
  "sniadanie", // breakfast
  "obiad", // lunch/dinner
  "kolacja", // supper
  "deser", // dessert
  "przekaska", // snack
  "napoj", // drink (smoothie, bulletproof coffee)

  // G. Sezonowość
  "sezonwiosna", // spring season (March-May) - seasonal Polish products
  "sezonlato", // summer season (June-August) - seasonal Polish products
  "sezonjesien", // autumn season (September-November) - seasonal Polish products
  "sezonzima", // winter season (December-February) - seasonal Polish products

  // Review tags (AI uncertainty flags)
  "dosprawdzeniaprzepis", // recipe needs human review
  "dosprawdzenianazwaposilku", // meal name needs human review
  "dosprawdzenianowyprzepis", // AI-generated recipe needs review
  "dosprawdzenianowyprzepis-blad", // AI-generated recipe has uncertainty in technique
] as const;

export type DishTag = (typeof dishTags)[number];

export const dishSizeTypes = [1, 2, 3];

export interface DishIngredient extends Ingredient {
  portion: number;
  portionCalories: number;
  measure: string;
  measureWeight: number;
  calories: number;
  id: string;
  ingredientId: string;
  hint: string | null;
  measureId: number | null;
  measureCapacity: string | null;
  multiplier?: number;
}

export interface Dish extends DishEntity {
  ingredients: DishIngredient[];
  type: "dish";
  media:
    | {
        url: string;
        type: string;
      }[]
    | null;
  metadata:
    | (Record<string, string | number | null | object> & {
        estimatedMacros?: { carbs: number; fat: number; protein: number };
        rawIngredients?: string;
      })
    | null;
  preparationSteps: string[];
  portionCount?: number;
}

export interface DishEmbedding extends DishEmbeddingEntity {
  ingredients: DishIngredient[];
  parentId: string;
  type: "dish";
  media:
    | {
        url: string;
        type: string;
      }[]
    | null;
  metadata: Record<string, string | number | null> | null;
  preparationSteps: string[];
}

export interface LatestSelectedProducts extends Omit<LatestSelectedProductsEntity, "products"> {
  products: (DishInMeal | IngredientWithPortion)[];
}
